import {
  FormEvent,
  memo,
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useStationVirtualKeyboard } from '@hooks/station';

interface StationContestModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Resolves once the entry is stored; rejecting shows the retry state */
  onSubmit: (email: string) => Promise<void>;
}

type Status = 'idle' | 'submitting' | 'success' | 'error';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// The station screen behind the overlay swipes between song variants on
// mouse/touch moves, so nothing from the modal may bubble up to it.
const stopPropagation = (event: SyntheticEvent) => event.stopPropagation();

const BUTTON_BASE =
  "px-6 py-4 rounded-full text-xl font-medium font-['DM_Sans'] leading-6 transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

export const StationContestModal = memo(
  ({ isOpen, onClose, onSubmit }: StationContestModalProps) => {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState<Status>('idle');
    const [showInvalid, setShowInvalid] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const { keyboardHeight, showKeyboard } = useStationVirtualKeyboard(isOpen);

    useEffect(() => {
      if (!isOpen) return;
      setEmail('');
      setStatus('idle');
      setShowInvalid(false);
      // Kiosk keyboards only appear once the field actually has focus
      const timer = window.setTimeout(() => showKeyboard(inputRef.current), 50);
      return () => window.clearTimeout(timer);
    }, [isOpen, showKeyboard]);

    const handleSubmit = useCallback(
      async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const trimmed = email.trim();
        if (!EMAIL_PATTERN.test(trimmed)) {
          setShowInvalid(true);
          return;
        }
        setShowInvalid(false);
        setStatus('submitting');
        try {
          await onSubmit(trimmed);
          setStatus('success');
        } catch {
          setStatus('error');
        }
      },
      [email, onSubmit],
    );

    if (!isOpen) return null;

    const isSubmitting = status === 'submitting';

    return createPortal(
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999999] p-6"
        role="dialog"
        aria-modal="true"
        aria-labelledby="station-contest-title"
        onMouseDown={stopPropagation}
        onMouseMove={stopPropagation}
        onMouseUp={stopPropagation}
        onTouchStart={stopPropagation}
        onTouchMove={stopPropagation}
        onTouchEnd={stopPropagation}
        // The keyboard lies over the page, so make room for it
        style={keyboardHeight ? { paddingBottom: keyboardHeight } : undefined}>
        <div className="w-full max-w-xl bg-[rgba(30,30,30,0.98)] border border-white/10 rounded-[24px] p-8 backdrop-blur-[20px] shadow-[0_8px_24px_rgba(0,0,0,0.4)] text-white font-['DM_Sans']">
          {status === 'success' ? (
            <div className="flex flex-col items-center text-center gap-4">
              <h2
                id="station-contest-title"
                className="text-[32px] font-medium leading-10 tracking-[-0.64px]">
                {t('station.contest.successTitle')}
              </h2>
              <p className="text-lg text-white/70 leading-6">
                {t('station.contest.successDescription')}
              </p>
              <button
                type="button"
                onClick={onClose}
                className={`${BUTTON_BASE} mt-2 bg-fuchsia-700 text-white hover:bg-fuchsia-800`}>
                {t('station.contest.done')}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate className="flex flex-col">
              <h2
                id="station-contest-title"
                className="text-[32px] font-medium leading-10 tracking-[-0.64px]">
                {t('station.contest.title')}
              </h2>
              <p className="mt-2 text-lg text-white/70 leading-6">
                {t('station.contest.description')}
              </p>
              <label
                htmlFor="station-contest-email"
                className="mt-6 mb-2 text-xs font-medium uppercase tracking-[0.5px] text-white/70">
                {t('station.contest.emailLabel')}
              </label>
              <input
                ref={inputRef}
                id="station-contest-email"
                name="email"
                type="email"
                inputMode="email"
                enterKeyHint="done"
                autoComplete="off"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                placeholder={t('station.contest.emailPlaceholder')}
                value={email}
                disabled={isSubmitting}
                aria-invalid={showInvalid}
                aria-describedby={
                  showInvalid ? 'station-contest-email-error' : undefined
                }
                onChange={(event) => {
                  setEmail(event.target.value);
                  if (showInvalid) setShowInvalid(false);
                }}
                className={`w-full px-5 py-4 rounded-2xl bg-white/10 border text-2xl text-white placeholder-white/40 outline-none transition-colors focus:border-fuchsia-500 disabled:opacity-60 ${
                  showInvalid ? 'border-red-400' : 'border-white/20'
                }`}
              />
              {showInvalid && (
                <p
                  id="station-contest-email-error"
                  role="alert"
                  className="mt-2 text-base text-red-400">
                  {t('station.contest.invalidEmail')}
                </p>
              )}
              {status === 'error' && (
                <p role="alert" className="mt-2 text-base text-red-400">
                  {t('station.contest.error')}
                </p>
              )}
              <p className="mt-4 text-sm text-white/50 leading-5">
                {t('station.contest.consent')}
              </p>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
                  className={`${BUTTON_BASE} bg-white/10 text-white hover:bg-white/15`}>
                  {t('station.contest.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`${BUTTON_BASE} bg-fuchsia-700 text-white hover:bg-fuchsia-800 shadow-[inset_0px_-3px_4.5px_0px_rgba(0,0,0,0.15)] shadow-[inset_0px_3px_4.5px_0px_rgba(255,150,217,0.50)]`}>
                  {isSubmitting
                    ? t('station.contest.submitting')
                    : t('station.contest.submit')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>,
      document.body,
    );
  },
);

StationContestModal.displayName = 'StationContestModal';
